import { useState, useEffect, useRef, useMemo } from "react";
import {
  TextField,
  Box,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "../../pages/edit/EditPage";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import {
  handleError,
  validateNonEmpty,
  fetchFinanceTypesPayments,
  removeNullsAndEmptys,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import { useConfig } from "../../providers/ConfigProvider";

const EditManualCash = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const { config, isLoadingConfig } = useConfig();
  const [isLoading, setIsLoading] = useState(true);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  
  const [cashMovement, setCashMovement] = useState({
    date: dayjs(),
    amount: "",
    finance_payment_method: null,
    reason: "",
    type: null,
  });

  const movementTypes = [
    { id: 1, name: t('common.income'), value: 'MANUAL_CASH_IN' },
    { id: 2, name: t('common.expense'), value: 'MANUAL_CASH_OUT' }
  ];

  const [financePaymentMethods, setFinancePaymentMethods] = useState([]);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    type: useRef(null),
    amount: useRef(null),
    finance_payment_method: useRef(null),
    date: useRef(null),
    reason: useRef(null),
  });  

  useEffect(() => {
    
    updateTitle(
      '',
      t('edit.manualCashTitle'),
      t('common.completeData')
    );

    toggleFab(false);

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const financeTypesPayments = await fetchFinanceTypesPayments();
        //console.log(financeTypesPayments);
        setFinancePaymentMethods(financeTypesPayments);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Set default type to 'Ingreso'
    setCashMovement(prev => ({
      ...prev,
      type: movementTypes[0]
    }));

    // Add a delay to focus to ensure component is fully rendered
    setTimeout(() => {
      if (refs.amount.current) {
        refs.amount.current.focus();
      }
    }, 100);
  }, []);

  // Set default payment method to 'Efectivo' when financePaymentMethods are loaded
  useEffect(() => {
    if (financePaymentMethods.length > 0) {
      const efectivoMethod = financePaymentMethods.find(method => method.name === 'Efectivo');
      if (efectivoMethod) {
        setCashMovement(prev => ({
          ...prev,
          finance_payment_method: efectivoMethod
        }));
      }
    }
  }, [financePaymentMethods]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCashMovement({ ...cashMovement, [name]: value });
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['date', 'amount', 'type', 'finance_payment_method', 'reason'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (
      !validateNonEmpty("type", cashMovement.type?.name, setErrors, refs.type) ||
      !validateNonEmpty("amount", cashMovement.amount, setErrors, refs.amount) ||
      !validateNonEmpty("finance_payment_method", cashMovement.finance_payment_method?.name, setErrors, refs.finance_payment_method)
    ) {
      return;
    }

    save();
  };

  const save = async () => {  
    setIsLoading(true);

    try {
      const dataToSend = {
        ...removeNullsAndEmptys(cashMovement),
        type: cashMovement.type.value, // Replace the type object with just the type value
        date: cashMovement.date.format('YYYY-MM-DD HH:mm:ss')
      };
      
      let response = await axios.post(
        `${import.meta.env.VITE_API_URL}cash_movements`,
        dataToSend,
        { withCredentials: true }
      );

      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isLoadingConfig}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
        <DateTimePicker
          label={t('common.dateTime')}
          value={cashMovement.date}
          onChange={(newValue) => setCashMovement({ ...cashMovement, date: newValue })}
          slotProps={{ 
            textField: { 
              fullWidth: true,
              margin: "normal",
              inputRef: refs.date,
              onKeyDown: (e) => handleKeyDown(e, 'date')
            } 
          }}
          format={dateTimePickerFormat}
          ampm={useHour12}
        />
      </LocalizationProvider>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.amount')}
        type="text"
        inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
        value={cashMovement.amount}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9.,]/g, "");
          setCashMovement({ ...cashMovement, amount: value });
        }}
        inputRef={refs.amount}
        error={!!errors.amount}
        helperText={errors.amount}
        onKeyDown={(e) => handleKeyDown(e, 'amount')}
      />

      <Autocomplete
        fullWidth
        options={movementTypes}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={cashMovement.type}
        onChange={(e, newValue) =>
          setCashMovement({
            ...cashMovement,
            type: newValue
          })
        }
        disableClearable
        forcePopupIcon
        disablePortal
        selectOnFocus
        disableCloseOnSelect={false}
        freeSolo={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.movementType')}
            name="type"
            margin="normal"
            inputRef={refs.type}
            error={!!errors.type}
            helperText={errors.type}
            onKeyDown={(e) => handleKeyDown(e, 'type')}
            InputProps={{
              ...params.InputProps,
              readOnly: true,
            }}
          />
        )}
      />

      <Autocomplete
        fullWidth
        options={financePaymentMethods}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={cashMovement.finance_payment_method}
        disableClearable={true}
        onChange={(e, newValue) =>
          setCashMovement({
            ...cashMovement,
            finance_payment_method: newValue
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.paymentMethod')}
            name="finance_payment_method"
            margin="normal"
            inputRef={refs.finance_payment_method}
            error={!!errors.finance_payment_method}
            helperText={errors.finance_payment_method}
            onKeyDown={(e) => handleKeyDown(e, 'finance_payment_method')}
          />
        )}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.motive')}
        name="reason"
        value={cashMovement.reason}
        onChange={handleChange}
        error={!!errors.reason}
        helperText={errors.reason}
        inputRef={refs.reason}
        inputProps={{ maxLength: 120 }}
        onKeyDown={(e) => handleKeyDown(e, 'reason')}
        sx={{ mb: 2 }}
      />
    </EditPage>
  );
};

export default EditManualCash;
